import React, { useEffect, useRef, useState } from "react";
import { TpmBar } from "../../home/chartKit";
import { HOME_SCALE } from "../../home/homeTheme";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import Map from "./Map";
import Filters from "./Filters";
import dayjs from "dayjs";
import { httpService } from "../../../../services/httpService.service";
import { Button } from "antd";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { toPng } from "html-to-image";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { isAuthorized } from "../../../../services/authService";
import { RolesEnum } from "../../../../services/rolesEnum";

// Register required components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const GeographicsTrends = () => {
  const [loadingData, setLoadingData] = useState(false);
  const [mapData, setMapData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const chartRef = useRef(null);
  const [filters, setFilters] = useState({
    country: "",
    state: "",
    startDate: "",
    endDate: "",
  });

  const fetchData = () => {
    setLoadingData(true);
    const projectId = localStorage.getItem("currentProjectID");

    const startDate = localStorage.getItem("Contract-startDate");
    const endDate = localStorage.getItem("Contract-endDate");

    const params = {
      ProjectId: projectId,
      StartDate: filters.startDate || dayjs(startDate).startOf("month"),
      EndDate: filters.endDate || dayjs(endDate).endOf("month"),
      Country: filters.country,
      State: filters.state,
    };

    const queryString = new URLSearchParams(params).toString();

    httpService.get(
      `/api/Site/geographicTrends?${queryString}`,
      (res) => {
        setMapData(res?.data?.siteVisitLongitudeLatitudeStatuses);
        setChartData(res?.data?.siteVisitStateStatusResponses);
        setLoadingData(false);
      },
      (err) => {
        setLoadingData(false);
      }
    );
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  const staticColors = {
    notInitiated: "#999",
    inProgress: HOME_SCALE.fair,
    completed: HOME_SCALE.good,
    postponed: HOME_SCALE.critical,
  };

  const generateColor = (key) => {
    const hash = key
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return `hsl(${hash % 360}, 70%, 60%)`;
  };

  const allStatusKeys = Array.from(
    new Set(
      chartData.flatMap((item) =>
        Object.keys(item).filter((key) => key !== "stateName")
      )
    )
  );

  const datasets = allStatusKeys.map((status) => ({
    label: status.replace(/([A-Z])/g, " $1").trim(),
    data: chartData.map((item) => item[status] || 0),
    backgroundColor: staticColors[status] || generateColor(status),
    borderColor: staticColors[status] || generateColor(status),
    borderWidth: 1,
  }));

  const accessibilityDistributionData = {
    labels: chartData.map((item) => item.stateName),
    datasets,
  };

  const accessibilityDistributionOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: {
        position: "bottom",
      },
      title: {
        display: true,
        text: "Count of Visits by State",
        font: {
          size: 18,
          weight: "600",
          style: "normal",
          family: "Poppins",
          lineHeight: "26px",
        },
        color: "#6A113C",
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
      },
    },
  };

  const handleExportExcel = () => {
    const projectId = localStorage.getItem("currentProjectID");
    const params = {
      projectId: projectId,
      startDate: filters.startDate || dayjs().startOf("month"),
      endDate: filters.endDate || dayjs().endOf("month"),
      ...(filters.state ? { country: filters.state } : {}),
      ipAnalysis: false,
      exportFilters: {
        includeGeographicTrends: true,
      },
    };

    httpService.post(
      `/api/Site/exportVisitDataSummary`,
      params,
      (response) => {
        const blob = new Blob([response], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "Geographic Trends.xlsx";
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      },
      (err) => {
        console.error(err);
      },
      { responseType: "arraybuffer" }
    );
  };

  const handleExportPDF = () => {
    if (!chartRef.current) return;

    html2canvas(chartRef.current, {
      useCORS: true,
      allowTaint: true,
      logging: true,
      scale: 2, // Increase quality
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF();
      const imgWidth = 180;
      const imgHeight = (canvas.height / canvas.width) * imgWidth;

      pdf.addImage(imgData, "PNG", 15, 15, imgWidth, imgHeight);
      pdf.save("GeographicMap.pdf");
    });
  };

  return (
    <>
      <div className="export-container row gap-2">
        <div className="col-12 col-lg-8">
          <Filters filters={filters} setFilters={setFilters} />
        </div>
        {isAuthorized([
          RolesEnum.SystemAdmin,
          RolesEnum.ProjectManager,
          RolesEnum.FieldCoordinator,
          RolesEnum.FieldTeamLeader,
        ]) && (
          <div className="row gap-2 col-12 col-lg-4">
            <Button
              className="exportButtonTPM addButtonTPM col-12 col-md-5 col-lg-5"
              style={{
                padding: "5px 20px",
                fontSize: "13px",
                maxHeight: "35px",
              }}
              icon={<DownloadOutlined />}
              onClick={handleExportExcel}
            >
              Export to Excel
            </Button>
            <Button
              className="exportButtonTPM addButtonTPM col-12 col-md-5 col-lg-5"
              style={{
                padding: "5px 20px",
                fontSize: "13px",
                maxHeight: "35px",
                background: " var(--2, #6A113C) !important",
              }}
              icon={<DownloadOutlined />}
              onClick={handleExportPDF}
            >
              Export to PDF
            </Button>
          </div>
        )}
      </div>
      <div ref={chartRef} className="charts-container">
        <div>
          <Map data={mapData} loadingData={loadingData} />
        </div>
        <div className="chart-card" style={{ width: "100%", height: "400px" }}>
          <TpmBar
            data={accessibilityDistributionData}
            options={accessibilityDistributionOptions}
          />
        </div>
      </div>
    </>
  );
};

export default GeographicsTrends;
